import type {
    GetDelegateTokenResponse,
    ValorCardTokenizationResponse,
} from '@pillar-portal/api-types';
import { ValorCardTokenizationResponseSchema } from '@pillar-portal/api-types';
import { buildValorCardTokenizationQueryParams } from './ipg/valor/card-tokenization';

export type CardBrand = 'Visa' | 'Mastercard' | 'AmEx' | 'Unknown';

export type CardFormData = {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
};

type CardBrandConfiguration = {
    brand: CardBrand;
    regex: RegExp;
    mask: string;
    securityCodeLength: number;
};

type TokenizationRequestParams = {
    cardData: CardFormData;
    delegateToken: GetDelegateTokenResponse;
};

const CardBrandConfigurations: ReadonlyArray<CardBrandConfiguration> = [
    {
        brand: 'Visa',
        regex: /^4/,
        mask: '0000 0000 0000 0000',
        securityCodeLength: 3,
    },
    {
        brand: 'Mastercard',
        regex: /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[0-1]|2720)/,
        mask: '0000 0000 0000 0000',
        securityCodeLength: 3,
    },
    {
        brand: 'AmEx',
        regex: /^3[47]/,
        mask: '0000 000000 00000',
        securityCodeLength: 4,
    },
];

export function detectCardBrandConfiguration(
    cardNumber: string
): CardBrandConfiguration | null {
    const normalizedPan = cardNumber.replace(/\D/g, '');
    return (
        CardBrandConfigurations.find((configuration) =>
            configuration.regex.test(normalizedPan)
        ) ?? null
    );
}

export function formatCardNumber(value: string): string {
    const normalizedDigits = value.replace(/\D/g, '');
    const detectedConfiguration = detectCardBrandConfiguration(normalizedDigits);
    const mask = detectedConfiguration?.mask ?? '0000 0000 0000 0000';
    const maskCharacters = mask.split('');
    let digitIndex = 0;
    let formattedValue = '';

    for (const maskCharacter of maskCharacters) {
        if (digitIndex >= normalizedDigits.length) {
            break;
        }
        if (maskCharacter === '0') {
            formattedValue += normalizedDigits[digitIndex] ?? '';
            digitIndex += 1;
            continue;
        }
        formattedValue += maskCharacter;
    }

    return formattedValue;
}

export function formatExpiryDate(value: string): string {
    const normalizedDigits = value.replace(/\D/g, '').slice(0, 4);
    if (normalizedDigits.length <= 2) {
        return normalizedDigits;
    }
    return `${normalizedDigits.slice(0, 2)}/${normalizedDigits.slice(2)}`;
}

export function resolveCardBrand(cardNumber: string): CardBrand {
    return detectCardBrandConfiguration(cardNumber)?.brand ?? 'Unknown';
}

export function validateCardFormData(cardData: CardFormData): string | null {
    const normalizedPan = cardData.cardNumber.replace(/\D/g, '');
    if (normalizedPan.length < 15 || normalizedPan.length > 16) {
        return 'Enter a valid card number.';
    }

    const detectedConfiguration = detectCardBrandConfiguration(cardData.cardNumber);
    const expectedCvvLength = detectedConfiguration?.securityCodeLength ?? 3;
    if (cardData.cvv.length !== expectedCvvLength) {
        return 'Enter a valid security code.';
    }

    if (!/^\d{2}\/\d{2}$/.test(cardData.expiryDate)) {
        return 'Enter a valid expiration date.';
    }

    if (cardData.cardholderName.trim().length === 0) {
        return 'Enter the cardholder name.';
    }

    return null;
}

export async function tokenizeCardWithDelegateToken(
    params: TokenizationRequestParams
): Promise<ValorCardTokenizationResponse> {
    const normalizedPan = params.cardData.cardNumber.replace(/\D/g, '');
    const normalizedExpiryDate = params.cardData.expiryDate.replace('/', '');
    const queryParams = buildValorCardTokenizationQueryParams({
        delegateToken: params.delegateToken,
        normalizedPan,
        normalizedExpiryDate,
    });

    const response = await fetch(`${params.delegateToken.domain}/?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
            accept: 'application/json',
        },
    });

    const responseBody: unknown = await response.json();
    if (!response.ok) {
        throw new Error(`Card tokenization failed with HTTP ${response.status}.`);
    }

    const parseResult = ValorCardTokenizationResponseSchema.safeParse(responseBody);
    if (!parseResult.success) {
        throw new Error('Card tokenization response is missing expected fields.');
    }

    return parseResult.data;
}
